import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { Subject, takeUntil } from "rxjs";

export interface VenueCourtCapacity {
  minCapacity?: number | null;
  maxCapacity?: number | null;
}

export const SPORT_MATCH_FORMATS: Record<string, string[]> = {
  badminton: ["Singles", "Doubles"],
  basketball: ["Half court (3-on-3)", "Full court (5-on-5)"],
  bowling: ["Singles", "Teams"],
  cricket: ["T20", "One Day", "Test"],
  padel: ["Singles", "Doubles"],
  paintball: ["3-a-side", "5-a-side"],
  pickleball: ["Singles", "Doubles"],
  futsal: ["5-a-side"],
  tabletennis: ["Singles", "Doubles"],
  tennis: ["Singles", "Doubles"],
  volleyball: ["Beach (2-on-2)", "Indoor (6-on-6)"],
  soccer: ["5-a-side", "7-a-side", "11-a-side"],
  football: ["5-a-side", "7-a-side", "11-a-side"],
};

const integerValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  if (control.value === null || control.value === "") return null;
  return Number.isInteger(Number(control.value)) ? null : { integer: true };
};

@Component({
  selector: "app-venue-booking-match-settings",
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
  ],
  templateUrl: "./venue-booking-match-settings.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VenueBookingMatchSettingsComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input({ required: true }) form!: UntypedFormGroup;
  @Input() court: VenueCourtCapacity | null = null;
  @Input() sport: string | null = null;
  @Input() formats: string[] = [];
  @Input() maintenance = false;
  @Input() occupiedPlayerSlots = 0;
  @Input() showMatchmakingToggle = false;

  private readonly destroyed = new Subject<void>();

  get isPublic(): boolean {
    return this.form?.get("bookingType")?.value === "public";
  }

  get matchFormats(): string[] {
    const sportKey = String(this.sport || "")
      .trim()
      .toLowerCase();
    const available = this.formats.length
      ? this.formats
      : SPORT_MATCH_FORMATS[sportKey] || [];
    return available.filter(
      (format, index, values) =>
        !!format?.trim() && values.indexOf(format) === index,
    );
  }

  get minCapacity(): number | null {
    const value = Number(this.court?.minCapacity);
    return Number.isInteger(value) && value >= 1 ? value : null;
  }

  get maxCapacity(): number | null {
    const value = Number(this.court?.maxCapacity);
    return Number.isInteger(value) && value >= 1 ? value : null;
  }

  get availablePlayerSlots(): number | null {
    const capacity = Number(this.form?.get("slots")?.value);
    if (!Number.isInteger(capacity) || capacity < 1) return null;
    return Math.max(0, capacity - Math.max(0, this.occupiedPlayerSlots));
  }

  get matchmakingUnavailable(): boolean {
    return (
      this.isPublic &&
      this.showMatchmakingToggle &&
      this.availablePlayerSlots === 0
    );
  }

  ngOnInit(): void {
    this.form
      .get("bookingType")
      ?.valueChanges.pipe(takeUntil(this.destroyed))
      .subscribe(() => this.reconcileControls());
    this.form
      .get("slots")
      ?.valueChanges.pipe(takeUntil(this.destroyed))
      .subscribe(() => this.reconcileControls());
    this.reconcileControls();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["court"] ||
      changes["sport"] ||
      changes["maintenance"] ||
      changes["formats"] ||
      changes["occupiedPlayerSlots"] ||
      changes["showMatchmakingToggle"]
    ) {
      this.reconcileControls();
    }
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  private reconcileControls(): void {
    if (!this.form) return;
    const visibility = this.form.get("bookingType");
    const capacity = this.form.get("slots");
    const format = this.form.get("format");
    const composition = this.form.get("composition");
    if (!visibility || !capacity || !format || !composition) return;

    if (this.maintenance) {
      visibility.setValue("private", { emitEvent: false });
      capacity.setValue(null, { emitEvent: false });
    }

    if (visibility.value !== "public") {
      this.form.get("startMatchmaking")?.setValue(false, { emitEvent: false });
      this.reconcileMatchmakingControl();
      capacity.clearValidators();
      capacity.updateValueAndValidity({ emitEvent: false });
      format.clearValidators();
      composition.clearValidators();
      format.updateValueAndValidity({ emitEvent: false });
      composition.updateValueAndValidity({ emitEvent: false });
      return;
    }

    const validators: ValidatorFn[] = [Validators.required, integerValidator];
    if (this.minCapacity !== null)
      validators.push(Validators.min(this.minCapacity));
    if (this.maxCapacity !== null)
      validators.push(Validators.max(this.maxCapacity));
    capacity.setValidators(validators);
    format.setValidators([Validators.required, Validators.pattern(/\S/)]);
    composition.setValidators(Validators.required);

    if (
      (capacity.value === null || capacity.value === "") &&
      this.maxCapacity !== null
    ) {
      capacity.setValue(this.maxCapacity, { emitEvent: false });
    }
    if (format.value && !this.matchFormats.includes(format.value)) {
      format.setValue("", { emitEvent: false });
    }
    capacity.updateValueAndValidity({ emitEvent: false });
    format.updateValueAndValidity({ emitEvent: false });
    composition.updateValueAndValidity({ emitEvent: false });
    this.reconcileMatchmakingControl();
  }

  private reconcileMatchmakingControl(): void {
    const control = this.form.get("startMatchmaking");
    if (!control) return;
    if (this.matchmakingUnavailable) {
      control.setValue(false, { emitEvent: false });
      control.disable({ emitEvent: false });
      return;
    }
    control.enable({ emitEvent: false });
  }
}
